import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, Input, Button, Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../../lib/apiClient.js';

// Payment Config (architecture.md §13, §24 M6). Super Admin defines manual-payment details
// (UPI, bank, QR, WhatsApp) shown to users on the Upgrade Plan page. Users pay, send a
// screenshot over WhatsApp, and the admin upgrades the plan by hand (Manage Users).
export default function AdminPayments() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ['admin-settings'], queryFn: api.adminGetSettings });
  const [form, setForm] = useState({ upi_id: '', bank_details: '', whatsapp_number: '', lifetime_deal_enabled: false, lifetime_deal_price: '' });
  const [qrUrl, setQrUrl] = useState('');
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [qrMsg, setQrMsg] = useState('');
  const [lifetimeQrUrl, setLifetimeQrUrl] = useState('');
  const [lifetimeUploading, setLifetimeUploading] = useState(false);
  const [lifetimeQrMsg, setLifetimeQrMsg] = useState('');

  useEffect(() => {
    const s = data?.settings;
    if (s) {
      setForm({
        upi_id: s.upi_id || '', bank_details: s.bank_details || '', whatsapp_number: s.whatsapp_number || '',
        lifetime_deal_enabled: !!s.lifetime_deal_enabled, lifetime_deal_price: s.lifetime_deal_price || '',
      });
      setQrUrl(s.payment_qr_url || '');
      setLifetimeQrUrl(s.lifetime_deal_qr_url || '');
    }
  }, [data]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => api.adminUpdateSettings(form),
    onSuccess: () => setMsg('Saved'),
    onError: (err) => setMsg(err.message),
  });

  const onPickQr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setQrMsg('');
    try {
      const res = await api.adminUploadPaymentQr(file);
      setQrUrl(res.payment_qr_url);
      setQrMsg('QR updated');
      await refetch();
    } catch (err) { setQrMsg(err.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const onPickLifetimeQr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLifetimeUploading(true); setLifetimeQrMsg('');
    try {
      const res = await api.adminUploadLifetimeDealQr(file);
      setLifetimeQrUrl(res.lifetime_deal_qr_url);
      setLifetimeQrMsg('QR updated');
      await refetch();
    } catch (err) { setLifetimeQrMsg(err.message); }
    finally { setLifetimeUploading(false); e.target.value = ''; }
  };

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold">Payment Config</h1>
      <p className="mb-6 text-sm text-slate-500">Manual payment details shown to users on the Upgrade Plan page.</p>

      <Card>
        <div className="grid gap-4">
          <Input label="UPI ID" placeholder="name@upi" value={form.upi_id} onChange={set('upi_id')} />

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Bank details</span>
            <textarea
              rows={4}
              placeholder={'Account name: …\nAccount no: …\nIFSC: …\nBank / Branch: …'}
              value={form.bank_details}
              onChange={set('bank_details')}
              className="w-full rounded-dc-sm border border-slate-400 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>

          <Input
            label="WhatsApp number"
            placeholder="+91XXXXXXXXXX"
            value={form.whatsapp_number}
            onChange={set('whatsapp_number')}
          />
          <p className="-mt-2 text-xs text-slate-400">Include country code. Users send their payment screenshot to this WhatsApp number.</p>

          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Payment QR code</span>
            <div className="flex items-center gap-4">
              {qrUrl ? (
                <img src={qrUrl} alt="Payment QR" className="h-24 w-24 rounded-lg border border-slate-400 object-contain" />
              ) : (
                <div className="grid h-24 w-24 place-items-center rounded-lg border border-dashed border-slate-300 text-xs text-slate-400">No QR</div>
              )}
              <label className="inline-flex cursor-pointer items-center gap-2">
                <span className="rounded-dc-sm bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800">Choose File</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickQr} disabled={uploading} />
                {uploading && <Spinner />}
                {qrMsg && <span className="text-xs text-slate-500">{qrMsg}</span>}
              </label>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? <Spinner /> : 'Save Payment Config'}</Button>
          {msg && <span className="text-sm text-slate-500">{msg}</span>}
        </div>
      </Card>

      <div className="mt-6">
        <Card>
          <h2 className="text-lg font-semibold">Lifetime Deal</h2>
          <p className="mb-4 text-sm text-slate-500">
            A one-time lifetime offer shown on the public <code className="rounded bg-slate-100 px-1">/deal</code> page.
            Same manual-UPI model — it uses the UPI ID & WhatsApp number above.
          </p>
          <div className="grid gap-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.lifetime_deal_enabled}
                onChange={(e) => setForm((f) => ({ ...f, lifetime_deal_enabled: e.target.checked }))}
                className="h-4 w-4 rounded border-slate-400 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-slate-700">Enable the lifetime deal (show it on /deal)</span>
            </label>

            <Input label="Price (display)" placeholder="$25" value={form.lifetime_deal_price} onChange={set('lifetime_deal_price')} />

            <div>
              <span className="mb-1 block text-sm font-medium text-slate-700">Lifetime deal QR code</span>
              <div className="flex items-center gap-4">
                {lifetimeQrUrl ? (
                  <img src={lifetimeQrUrl} alt="Lifetime deal QR" className="h-24 w-24 rounded-lg border border-slate-400 object-contain" />
                ) : (
                  <div className="grid h-24 w-24 place-items-center rounded-lg border border-dashed border-slate-300 text-xs text-slate-400">No QR</div>
                )}
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <span className="rounded-dc-sm bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800">Choose File</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPickLifetimeQr} disabled={lifetimeUploading} />
                  {lifetimeUploading && <Spinner />}
                  {lifetimeQrMsg && <span className="text-xs text-slate-500">{lifetimeQrMsg}</span>}
                </label>
              </div>
              <p className="mt-2 text-xs text-slate-400">After a customer pays & sends their screenshot, grant lifetime access from Manage Users → “Grant Lifetime”.</p>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? <Spinner /> : 'Save Lifetime Deal'}</Button>
            {msg && <span className="text-sm text-slate-500">{msg}</span>}
          </div>
        </Card>
      </div>
    </div>
  );
}
